import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useMoney } from "../../../../../currency/useMoney"
import { CustomerSalesByAccountResponse } from "../../accountingApi"
import { Box } from "@mui/system"
import { Skeleton, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from "@mui/material"
import { isEmpty } from "lodash"
import { ExpandableTableRow } from "../ExpandableTableRow"
import { toDinero } from "../../../../../currency/currencyLib"
import { UseQueryResult } from "@tanstack/react-query"
import { TableSummaryWrap, TableTotalAmountBox } from "../../../../../../shared/components/table/TableSummaryBox"
import { makeStyles } from "tss-react/mui"

type CustomerAccordionDetailsProps = Readonly<{
    salesByAccounts: UseQueryResult<CustomerSalesByAccountResponse, unknown>
}>

const useStyles = makeStyles()(() => ({
    table: {
        borderRadius: ".75rem",
        overflow: "hidden",
        boxShadow: "0px 2px 6px 0px #00000014",
    },
    subRowProduct: {
        marginLeft: "3.5rem",
    },
}))

export function CustomerAccordionDetails({ salesByAccounts }: CustomerAccordionDetailsProps) {
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})

    const { classes } = useStyles()

    if (salesByAccounts.isLoading) {
        return (
            <Box sx={{ width: "100%" }}>
                <Skeleton />
                <Skeleton />
                <Skeleton />
            </Box>
        )
    }

    if (isEmpty(salesByAccounts.data?.salesByAccount.categories)) {
        return <></>
    }

    return (
        <>
            {salesByAccounts.data?.salesByAccount.categories.map((category) => {
                return (
                    <>
                        <Typography
                            variant="body2"
                            gutterBottom
                            marginBottom={"1rem"}
                            marginTop={"2rem"}
                            sx={{ fontWeight: "bold" }}
                        >
                            {category.name}
                        </Typography>

                        <Table className={classes.table}>
                            <TableHead sx={{ backgroundColor: "#F8FAFC" }}>
                                <TableRow>
                                    <TableCell></TableCell>

                                    <TableCell sx={{ textAlign: "end" }}>
                                        {category.accounts.some((acc) => {
                                            return acc.products && acc.products.length > 0
                                        }) && l10n.getString("accounting-product-sale-product-quantity")}
                                    </TableCell>

                                    <TableCell sx={{ textAlign: "end", width: "250px" }}>
                                        {l10n.getString("accounting-amount")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {category.accounts.map((customerAccount) => {
                                    return (
                                        <>
                                            {customerAccount.products ? (
                                                <ExpandableTableRow
                                                    cellValue={customerAccount.name}
                                                    cellValue2={moneyFactory.format(toDinero(customerAccount.amount))}
                                                >
                                                    {customerAccount.products &&
                                                        customerAccount.products.map((product) => {
                                                            return (
                                                                <TableRow>
                                                                    <TableCell>
                                                                        <Typography
                                                                            className={classes.subRowProduct}
                                                                            variant="body2"
                                                                            color="GrayText"
                                                                        >
                                                                            {product.name}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            textAlign: "end",
                                                                            color: "GrayText",
                                                                        }}
                                                                    >
                                                                        {product.numberOfItems}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            textAlign: "end",
                                                                            color: "GrayText",
                                                                        }}
                                                                    >
                                                                        {moneyFactory.format(
                                                                            toDinero(product.totalAmount),
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        })}
                                                </ExpandableTableRow>
                                            ) : (
                                                <TableRow>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                marginLeft: "2.4rem",
                                                            }}
                                                        >
                                                            {customerAccount.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: "end" }}></TableCell>
                                                    <TableCell sx={{ textAlign: "end" }}>
                                                        {moneyFactory.format(toDinero(customerAccount.amount))}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    )
                                })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={3} sx={{ padding: "0" }}>
                                        <TableSummaryWrap padding="sm">
                                            <TableTotalAmountBox
                                                data={category?.accounts}
                                                variant="embedded"
                                                accessor={(row) => row.amount}
                                            />
                                        </TableSummaryWrap>
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </>
                )
            })}
        </>
    )
}
